import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { JobsService } from '../../services/jobs.service';
import { VerifyResult } from '../../models/verify.model';

/**
 * Adapter that validates a scanned or manually entered verification code
 * against the document ID stored in `Job.verCodes[defendantIndex]`.
 *
 * @remarks
 * No HTTP call is made — verification is performed locally using the
 * `service_documents[].document_id` values mapped into `Job.verCodes` by
 * `job.adapter.ts`.  The adapter looks up the expected code from
 * `JobsService.getJob()` so callers only need to supply the job ID,
 * defendant index, and the entered code.
 *
 * The returned Observable always emits exactly one value and completes;
 * callers never need to handle an error channel.
 *
 * @example
 * ```ts
 * this.verifyAdapter.verify(job.jobNum, 0, scannedCode).subscribe(result => {
 *   if (result.verified) { ... }
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class VerifyAdapter {
  private readonly jobsSvc = inject(JobsService);

  /**
   * Validates `verificationCode` against the document ID stored in
   * `job.verCodes[defendantIndex]` and returns a single-emission Observable.
   *
   * Rules applied in order:
   * 1. Job not found or no document ID for this defendant → `verified: false`
   * 2. Empty / whitespace-only code → `verified: false`
   * 3. Exact case-sensitive match → `verified: true`; mismatch → `verified: false`
   *
   * @param jobId            - Job number (`job.jobNum`) used to look up the job.
   * @param defendantIndex   - Zero-based index of the defendant in `job.defs`.
   * @param verificationCode - The barcode text or manually typed code.
   */
  verify(jobId: string, defendantIndex: number, verificationCode: string): Observable<VerifyResult> {
    const expected = this.jobsSvc.getJob(jobId)?.verCodes[defendantIndex] ?? '';

    if (!expected) {
      return of(this.result(jobId, false, 'No verification code configured for this defendant.'));
    }

    const trimmed = verificationCode.trim();
    if (!trimmed) {
      return of(this.result(jobId, false, 'Please enter a verification code.'));
    }

    const verified = trimmed === expected;
    return of(this.result(
      jobId,
      verified,
      verified ? '' : 'Code does not match. Please try again or enter manually.',
    ));
  }

  /**
   * Builds a {@link VerifyResult} with the given outcome.
   *
   * @param jobId    - Job number forwarded into the result.
   * @param verified - Whether the code matched.
   * @param message  - User-facing message; empty string on success.
   */
  private result(jobId: string, verified: boolean, message: string): VerifyResult {
    return { verified, jobId, defendant: '', message };
  }
}
